import { Center, createStyles, Menu } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { getBillingCache, deleteBillingCache } from "../../services";
import { useSelector } from "react-redux";
import TableLoader from "../TableLoader";
import ClaimFilter from "./ClaimFilter";
import CustomBillingPagination from "../Pagination/CustomBillingPagination";
import RecordNotFound from "../RecordNotFound";
import { useNavigate } from "react-router-dom";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { decrypt } from "../../utils/index";

const useStyles = createStyles(() => ({
  claimsTblWrap: { overflowX: "auto" },
  claimsTbl: {
    width: "100%",
    minWidth: "900px",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "14px",
    fontFamily: "Inter, sans-serif",
    "& thead th": {
      background: "var(--app-border)",
      color: "var(--app-text)",
      fontWeight: 600,
      fontSize: "12px",
      padding: "12px 14px",
      textAlign: "left",
      whiteSpace: "nowrap",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
    },
    "& thead th:first-of-type": {
      borderTopLeftRadius: "8px",
      borderBottomLeftRadius: "8px",
    },
    "& thead th:last-child": {
      borderTopRightRadius: "8px",
      borderBottomRightRadius: "8px",
    },
    "& tbody td": { padding: "14px", borderBottom: "1px solid var(--app-border)" },
  },

  // Batch row (draft)
  batchRow: {
    background: "var(--app-surface)",
    cursor: "pointer",
    fontWeight: 500,
    transition: "background .12s ease, opacity .12s ease",
    "& td": {
      borderTop: "1px solid var(--app-border)",
      paddingTop: "16px",
      paddingBottom: "16px",
    },
    "& td:first-of-type": {
      width: "36px",
      paddingLeft: "14px",
      paddingRight: 0,
    },
    "&:hover td": { background: "var(--app-surface-2)" },
  },
  batchRowDraft: {
    opacity: 0.72,
    "&:hover": { opacity: 0.9 },
    "&:hover td": { background: "var(--app-surface-2)" },
  },
  batchRowExpanded: {
    "& td": { borderBottom: "1px solid var(--app-border)" },
  },

  // Expand chevron
  expandChev: {
    width: "22px",
    height: "22px",
    border: 0,
    background: "transparent",
    cursor: "pointer",
    color: "var(--app-text-muted)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform .15s ease",
    borderRadius: "4px",
    "& svg": { width: "14px", height: "14px" },
    "&:hover": { background: "rgba(0,0,0,0.06)" },
  },
  expandChevOpen: { transform: "rotate(90deg)" },

  claimId: {
    fontFamily: "ui-monospace, 'SFMono-Regular', Menlo, monospace",
    fontSize: "13px",
    color: "var(--app-text-muted)",
    fontWeight: 600,
  },
  batchMeta: {
    fontSize: "11px",
    color: "var(--app-text-muted)",
    marginTop: "2px",
    fontWeight: 500,
    fontFamily: "Inter, sans-serif",
  },
  dimCell: { color: "var(--app-text-muted)", fontFamily: "Inter, sans-serif" },
  amtCell: {
    textAlign: "right",
    fontWeight: 500,
    fontVariantNumeric: "tabular-nums",
    fontFamily: "Inter, sans-serif",
  },

  // Status pips
  batchStatus: {
    display: "inline-flex",
    flexDirection: "column",
    gap: "6px",
    minWidth: "132px",
  },
  batchStatusPips: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    flexWrap: "wrap",
  },
  pip: {
    width: "10px",
    height: "10px",
    borderRadius: "3px",
    flexShrink: 0,
  },
  pipDraft: { background: "var(--app-surface-2)" },
  batchStatusSummary: {
    display: "inline-flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "6px",
    fontSize: "11px",
    fontWeight: 600,
    color: "var(--app-text-muted)",
    lineHeight: 1.35,
    fontFamily: "Inter, sans-serif",
  },
  summaryBold: { fontWeight: 800, marginRight: "3px" },

  // Child rows
  childRow: {
    fontSize: "12.5px",
    cursor: "pointer",
    transition: "background .1s ease",
    background: "var(--app-surface-2)",
    "& td": {
      color: "var(--app-text)",
      borderTop: "1px solid rgba(15,15,20,0.04)",
      paddingTop: "9px",
      paddingBottom: "9px",
    },
    "& td:first-of-type": {
      width: "36px",
      paddingLeft: "14px",
      paddingRight: 0,
    },
    "& td:nth-child(2)": { paddingLeft: "32px", position: "relative" },
    "& td:nth-child(2)::before": {
      content: '""',
      position: "absolute",
      left: "18px",
      top: "50%",
      width: "8px",
      height: "1px",
      background: "rgba(15,15,20,0.18)",
    },
    "&:hover td": { background: "var(--app-surface-2)" },
  },

  // Client cell
  clientCell: { display: "flex", alignItems: "center", gap: "10px" },
  clientAv: {
    width: "32px",
    height: "32px",
    borderRadius: "999px",
    background: "var(--app-surface-2)",
    color: "var(--app-text-muted)",
    fontSize: "12px",
    fontWeight: 700,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    overflow: "hidden",
  },
  clientAvImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "999px",
  },
  clientName: { fontWeight: 500, color: "var(--app-text)", fontSize: "13px" },

  // Status cell
  statusCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    width: "100%",
  },
  draftLabel: {
    fontSize: "11.5px",
    fontWeight: 600,
    color: "var(--app-text-muted)",
    fontStyle: "italic",
    fontFamily: "Inter, sans-serif",
  },

  // Child row menu
  childRowMenuBtn: {
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    background: "transparent",
    border: 0,
    color: "var(--app-text-muted)",
    cursor: "pointer",
    display: "inline-grid",
    placeItems: "center",
    "&:hover": { background: "rgba(0,0,0,0.06)", color: "var(--app-text)" },
  },
  childRowMenuIcon: { width: "14px", height: "14px" },

  // Pager
  pagerWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderTop: "1px solid var(--app-border)",
    background: "var(--app-surface-2)",
    flexWrap: "wrap",
    gap: "10px",
  },
  pagerInfo: {
    fontSize: "12.5px",
    color: "var(--app-text-muted)",
    fontWeight: 500,
    fontFamily: "Inter, sans-serif",
  },

  notFoundWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    gap: "16px",
  },
  notFoundText: {
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
    fontSize: "15px",
    fontWeight: 400,
    textAlign: "center",
  },
}));

function DraftClaims({ onContinueDraft, onDeleteDraft }) {
  const { classes, cx } = useStyles();
  const [filtermodal, setFilterModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedBatches, setExpandedBatches] = useState({});
  const { billingcache, loading } = useSelector((state) => state?.billing);
  const navigate = useNavigate();
  // Tracks when a menu action just fired so the ghost-click on the underlying
  // row (caused by Mantine portal teardown) is swallowed.
  const lastMenuActionAt = useRef(0);

  useEffect(() => {
    getBillingCache({ page: currentPage });
  }, [currentPage]);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const toggleBatch = (id) => {
    setExpandedBatches((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleContinueDraft = (item) => {
    if (Date.now() - lastMenuActionAt.current < 300) return;
    if (onContinueDraft) {
      onContinueDraft(item);
    } else {
      navigate("/home/billing-claim-form", { state: { billingItem: item } });
    }
  };

  const handleDeleteDraft = async (item) => {
    lastMenuActionAt.current = Date.now();
    if (onDeleteDraft) {
      onDeleteDraft(item.id || item._id);
    } else {
      await deleteBillingCache(item.id || item._id);
    }
  };

  const drafts = billingcache?.billingcache || [];

  const getClientName = (client) => {
    try {
      const raw = client?.name;
      if (!raw) return "—";
      return decrypt(raw) || "—";
    } catch {
      return client?.name || "—";
    }
  };

  const getClientImage = (client) => {
    try {
      const raw = client?.imageurl;
      if (!raw) return null;
      return decrypt(raw) || null;
    } catch {
      return null;
    }
  };

  const getInitials = (name) => {
    if (!name || name === "—") return "?";
    return name
      .split(" ")
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  return (
    <>
      <div className={classes.claimsTblWrap}>
        {loading ? (
          <TableLoader />
        ) : drafts.length > 0 ? (
          <table className={classes.claimsTbl}>
            <thead>
              <tr>
                <th></th>
                <th>Claim / Batch</th>
                <th>Clients</th>
                <th>Payer</th>
                <th>Provider</th>
                <th className={classes.amtCell}>Amount</th>
                <th>Provider Type</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((item, index) => {
                const batchId = item?._id || `batch-${index}`;
                const isExpanded = !!expandedBatches[batchId];
                const clientCount = item?.client?.length ?? 0;
                const serviceCount = item?.billingServices?.length ?? 0;
                const payerName = item?.payer?.payerName || "—";
                const providerName = item?.provider?.name || "—";
                const providerType = item?.provider?.providertype || "—";

                return (
                  <>
                    {/* Batch row */}
                    <tr
                      key={`batch-${batchId}`}
                      className={cx(
                        classes.batchRow,
                        classes.batchRowDraft,
                        isExpanded && classes.batchRowExpanded,
                      )}
                      onClick={() => toggleBatch(batchId)}
                    >
                      <td>
                        <button
                          className={cx(
                            classes.expandChev,
                            isExpanded && classes.expandChevOpen,
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleBatch(batchId);
                          }}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </button>
                      </td>
                      <td>
                        <span className={classes.claimId}>837p</span>
                        <div className={classes.batchMeta}>
                          {serviceCount} service{serviceCount !== 1 ? "s" : ""}{" "}
                          · {clientCount} client{clientCount !== 1 ? "s" : ""}
                        </div>
                      </td>
                      <td>
                        {clientCount} client{clientCount !== 1 ? "s" : ""}
                      </td>
                      <td className={classes.dimCell}>{payerName}</td>
                      <td className={classes.dimCell}>{providerName}</td>
                      <td className={cx(classes.dimCell, classes.amtCell)}>
                        —
                      </td>
                      <td className={classes.dimCell}>{providerType}</td>
                      <td>
                        <div className={classes.batchStatus}>
                          <div className={classes.batchStatusPips}>
                            {Array.from({
                              length: Math.max(1, serviceCount),
                            }).map((_, i) => (
                              <span
                                key={i}
                                className={cx(classes.pip, classes.pipDraft)}
                              />
                            ))}
                          </div>
                          <div className={classes.batchStatusSummary}>
                            <span>
                              <b className={classes.summaryBold}>
                                {serviceCount}
                              </b>{" "}
                              draft
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>

                    {/* Child rows (clients) */}
                    {isExpanded &&
                      (clientCount > 0 ? (
                        item.client.map((client, ci) => {
                          const clientName = getClientName(client);
                          const clientImage = getClientImage(client);
                          return (
                            <tr
                              key={`child-${batchId}-${ci}`}
                              className={classes.childRow}
                              onClick={() => handleContinueDraft(item)}
                            >
                              <td></td>
                              <td>
                                <span className={classes.claimId}>
                                  CLM-DRAFT-{String(index + 1).padStart(4, "0")}
                                  {ci}
                                </span>
                              </td>
                              <td>
                                <div className={classes.clientCell}>
                                  <div className={classes.clientAv}>
                                    {clientImage ? (
                                      <img src={clientImage} alt={clientName} className={classes.clientAvImg} />
                                    ) : (
                                      getInitials(clientName)
                                    )}
                                  </div>
                                  <span className={classes.clientName}>
                                    {clientName}
                                  </span>
                                </div>
                              </td>
                              <td className={classes.dimCell}>{payerName}</td>
                              <td className={classes.dimCell}>
                                {providerName}
                              </td>
                              <td
                                className={cx(classes.dimCell, classes.amtCell)}
                              >
                                —
                              </td>
                              <td className={classes.dimCell}>—</td>
                              <td>
                                <div className={classes.statusCell}>
                                  <span className={classes.draftLabel}>
                                    Draft
                                  </span>
                                  <Menu
                                    shadow="md"
                                    width={160}
                                    position="bottom-end"
                                    withinPortal
                                  >
                                    <Menu.Target>
                                      <button
                                        className={classes.childRowMenuBtn}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <BiDotsVerticalRounded
                                          className={classes.childRowMenuIcon}
                                        />
                                      </button>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                      <Menu.Item
                                        ff="Inter"
                                        fz={13}
                                        fw={500}
                                        color="var(--app-text)"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleContinueDraft(item);
                                        }}
                                      >
                                        Continue Draft
                                      </Menu.Item>
                                      <Menu.Item
                                        ff="Inter"
                                        fz={13}
                                        fw={500}
                                        color="#B91C1C"
                                        onClick={() => handleDeleteDraft(item)}
                                      >
                                        Delete
                                      </Menu.Item>
                                    </Menu.Dropdown>
                                  </Menu>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr
                          key={`child-${batchId}-0`}
                          className={classes.childRow}
                          onClick={() => handleContinueDraft(item)}
                        >
                          <td></td>
                          <td>
                            <span className={classes.claimId}>
                              CLM-DRAFT-{String(index + 1).padStart(4, "0")}
                            </span>
                          </td>
                          <td className={classes.dimCell}>—</td>
                          <td className={classes.dimCell}>{payerName}</td>
                          <td className={classes.dimCell}>{providerName}</td>
                          <td className={cx(classes.dimCell, classes.amtCell)}>
                            —
                          </td>
                          <td className={classes.dimCell}>—</td>
                          <td>
                            <div className={classes.statusCell}>
                              <span className={classes.draftLabel}>Draft</span>
                              <Menu
                                shadow="md"
                                width={160}
                                position="bottom-end"
                                withinPortal
                              >
                                <Menu.Target>
                                  <button
                                    className={classes.childRowMenuBtn}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <BiDotsVerticalRounded
                                      className={classes.childRowMenuIcon}
                                    />
                                  </button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                  <Menu.Item
                                    ff="Inter"
                                    fz={13}
                                    fw={500}
                                    color="var(--app-text)"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleContinueDraft(item);
                                    }}
                                  >
                                    Continue Draft
                                  </Menu.Item>
                                  <Menu.Item
                                    ff="Inter"
                                    fz={13}
                                    fw={500}
                                    color="#B91C1C"
                                  >
                                    Delete
                                  </Menu.Item>
                                </Menu.Dropdown>
                              </Menu>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className={classes.notFoundWrap}>
            <RecordNotFound />
            <p className={classes.notFoundText}>No draft claims found</p>
          </div>
        )}
      </div>

      <div className={classes.pagerWrap}>
        <div className={classes.pagerInfo}>
          {billingcache?.count
            ? `${billingcache.count} draft${billingcache.count !== 1 ? "s" : ""}`
            : ""}
        </div>
        <Center>
          <CustomBillingPagination
            data={billingcache?.count}
            itemsPerPage={10}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
          />
        </Center>
      </div>

      <ClaimFilter filtermodal={filtermodal} setFilterModal={setFilterModal} />
    </>
  );
}

export default DraftClaims;
